import moment from 'moment';
import { ComponentProps, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useQueryParameters } from '@treams/treams-hooks';
import { useAnalyticsInterval } from '@treams/treams-hooks/src/useAnalyticsInterval';

import { Input } from '../form/controls/Input';
import { ChevronLeftIcon, ChevronRightIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { FlexGap } from './FlexGap';
import { Select } from './Select';
import { Skeleton } from './Skeleton';
import { IconButton } from './button/IconButton';

type TimeInterval = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

const TIME_INTERVALS: TimeInterval[] = ['day', 'week', 'month', 'quarter', 'year', 'custom'];

function isTimeInterval(value: string | number | undefined): value is TimeInterval {
	return typeof value === 'string' && TIME_INTERVALS.includes(value as TimeInterval);
}

export interface ITimeMachine {
	current: [Date, Date];
	urlSafeCurrent: [string, string];
	previous?: Date;
	next?: Date;
	interval: TimeInterval;
}

export function createTimeMachineFromQuery() {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const queryParameters = useQueryParameters(['cursor', 'interval', 'end']);
	const interval = queryParameters.interval as TimeInterval;

	if (interval === 'custom') {
		return createCustomTimeMachine(
			moment(queryParameters.cursor).toDate(),
			moment(queryParameters.end).toDate(),
		);
	}

	return createTimeMachine(
		interval ?? 'month',
		queryParameters.cursor ? moment(queryParameters.cursor).toDate() : moment().toDate(),
	);
}

export function createCustomTimeMachine(cursor: Date, end: Date): ITimeMachine {
	const current: [Date, Date] = [
		moment(cursor).startOf('day').toDate(),
		moment(end).startOf('day').toDate(),
	];

	return {
		current,
		previous: moment(current[0])
			.subtract(moment(current[1]).diff(moment(current[0])), 'ms')
			.toDate(),
		next: undefined,
		interval: 'custom',
		urlSafeCurrent: [
			moment(current[0]).format('YYYY-MM-DD'),
			moment(current[1]).format('YYYY-MM-DD'),
		],
	};
}

export function createTimeMachine(
	interval: Exclude<TimeInterval, 'custom'>,
	cursor: Date,
	workspaceStartDate?: Date,
	allowFutureDates?: boolean,
): ITimeMachine {
	let current: [Date, Date];
	let previous: Date;
	let next: Date;

	if (interval === 'day') {
		const m = moment(cursor).startOf('day');
		current = [m.toDate(), m.clone().endOf('day').toDate()];
		previous = m.clone().subtract(1, 'day').toDate();
		next = m.clone().add(1, 'day').toDate();
	} else if (interval === 'week') {
		const m = moment(cursor).startOf('isoWeek');
		current = [m.toDate(), m.clone().endOf('isoWeek').toDate()];
		previous = m.clone().subtract(1, 'week').toDate();
		next = m.clone().add(1, 'week').toDate();
	} else {
		current = [moment(cursor).startOf(interval).toDate(), moment(cursor).endOf(interval).toDate()];
		previous = moment(cursor).startOf(interval).subtract(1, interval).toDate();
		next = moment(cursor).startOf(interval).add(1, interval).toDate();
	}

	return {
		current,
		previous:
			!workspaceStartDate || moment(current[0]).isAfter(moment(workspaceStartDate))
				? previous
				: undefined,
		next: moment(current[1]).isAfter(moment()) && !allowFutureDates ? undefined : next,
		interval,
		urlSafeCurrent: [
			moment(current[0]).format('YYYY-MM-DD'),
			moment(current[1]).format('YYYY-MM-DD'),
		],
	};
}

// Both dates are YYYY-MM-DD and never later than today. A month that is still
// running is read as today, not as the last day of that month.
export function timeMachineLocalDates(timeMachine: ITimeMachine): {
	localDate: string;
	previousLocalDate: string | undefined;
} {
	const today = moment().format('YYYY-MM-DD');
	const clamp = (date: string) => (date > today ? today : date);
	const localDate = clamp(timeMachine.urlSafeCurrent[1]);

	if (!timeMachine.previous) {
		return { localDate, previousLocalDate: undefined };
	}

	const dayBeforeStart = moment(timeMachine.urlSafeCurrent[0])
		.subtract(1, 'day')
		.format('YYYY-MM-DD');

	return { localDate, previousLocalDate: clamp(dayBeforeStart) };
}

function formatPeriodLabel(
	interval: Exclude<TimeInterval, 'custom'>,
	start: moment.Moment,
	end: moment.Moment,
) {
	if (interval === 'day') {
		return start.format('LL');
	}
	if (interval === 'week') {
		return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
	}
	if (interval === 'month') {
		return start.format('MMMM YYYY');
	}
	if (interval === 'quarter') {
		return `Q${start.format('Q YYYY')}`;
	}
	return start.format('YYYY');
}

function periodSelectOptions(
	interval: Exclude<TimeInterval, 'custom'>,
	current: Date,
	workspaceStartDate?: Date,
	allowFutureDates?: boolean,
) {
	const unit = interval === 'week' ? 'isoWeek' : interval;
	const radius =
		interval === 'day'
			? 45
			: interval === 'week'
				? 52
				: interval === 'month'
					? 60
					: interval === 'quarter'
						? 40
						: 15;

	const currentStart = moment(current).startOf(unit);
	const earliest = moment(
		workspaceStartDate ?? currentStart.clone().subtract(radius, interval),
	).startOf(unit);
	const start = moment.max(earliest, currentStart.clone().subtract(radius, interval));
	const latest = allowFutureDates
		? currentStart.clone().add(radius, interval)
		: moment.min(currentStart, moment().startOf(unit));

	const options: { title: string; value: string }[] = [];
	const cursor = start.clone();
	while (cursor.isSameOrBefore(latest)) {
		options.push({
			title: formatPeriodLabel(interval, cursor, cursor.clone().endOf(unit)),
			value: cursor.format('YYYY-MM-DD'),
		});
		cursor.add(1, interval);
	}

	return options;
}

const Navigator = styled(Flex)`
	align-items: center;
	flex-shrink: 0;
	gap: ${tokens.gap.inline}px;
`;

const CompactSelect = styled.div`
	flex-shrink: 0;
	width: max-content;

	> div {
		width: max-content;
	}
`;

export function DateIntervalValue(props: { value: ITimeMachine }) {
	if (props.value.interval === 'custom') {
		return null;
	}

	const start = moment(props.value.current[0]);
	const end = moment(props.value.current[1]);
	return <>{formatPeriodLabel(props.value.interval, start, end)}</>;
}

type DateIntervalNavigationProps = Omit<ComponentProps<typeof FlexGap>, 'children'> & {
	value: ITimeMachine;
	onChange: (timeMachine: ITimeMachine) => void;
	workspaceStartDate?: Date;
	disableQuery?: boolean;
	allowFutureDates?: boolean;
};

export function DateIntervalNavigation({
	onChange,
	value,
	workspaceStartDate,
	disableQuery,
	allowFutureDates,
	...rest
}: DateIntervalNavigationProps) {
	const history = useHistory();
	const { setInterval, setStartDate, setEndDate } = useAnalyticsInterval();

	useEffect(() => {
		if (disableQuery) {
			return;
		}

		const searchParams = new URLSearchParams(history.location.search);

		if (
			searchParams.get('interval') === value.interval &&
			searchParams.get('cursor') === value.urlSafeCurrent[0] &&
			searchParams.get('end') === value.urlSafeCurrent[1]
		) {
			return;
		}

		searchParams.set('interval', value.interval);
		searchParams.set('cursor', value.urlSafeCurrent[0]);
		searchParams.set('end', value.urlSafeCurrent[1]);

		history.push(`${history.location.pathname}?${searchParams.toString()}`);
		// history.location is read when value changes; depending on history would push again.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const { t } = useTranslation();

	const periodOptions = useMemo(() => {
		if (value.interval === 'custom') {
			return [];
		}

		return periodSelectOptions(
			value.interval,
			value.current[0],
			workspaceStartDate,
			allowFutureDates,
		);
	}, [value, workspaceStartDate, allowFutureDates]);

	function goTo(cursor: Date) {
		if (value.interval === 'custom') {
			return;
		}

		onChange(createTimeMachine(value.interval, cursor, workspaceStartDate, allowFutureDates));
		setInterval(value.interval);
	}

	return (
		<FlexGap
			flexDirection={['column', 'row']}
			alignItems={['flex-start', 'center']}
			flexWrap="wrap"
			flexShrink={0}
			gap={tokens.gap.list}
			{...rest}
		>
			<CompactSelect>
				<Select
					fit
					options={[
						{ title: t('dateInterval.interval.day'), value: 'day' },
						{ title: t('dateInterval.interval.week'), value: 'week' },
						{ title: t('dateInterval.interval.month'), value: 'month' },
						{ title: t('dateInterval.interval.quarter'), value: 'quarter' },
						{ title: t('dateInterval.interval.year'), value: 'year' },
						undefined,
						{ title: t('dateInterval.interval.custom'), value: 'custom' },
					]}
					selectedValue={value.interval}
					search={false}
					onChange={(interval) => {
						if (!isTimeInterval(interval)) {
							return;
						}

						if (interval === 'custom') {
							onChange(createCustomTimeMachine(...value.current));
							setInterval(interval);
						} else {
							onChange(
								createTimeMachine(
									interval,
									moment().toDate(),
									workspaceStartDate,
									allowFutureDates,
								),
							);
							setInterval(interval);
						}
					}}
				/>
			</CompactSelect>

			{value.interval === 'custom' ? (
				<>
					<Box width={140}>
						<Input.DatePicker
							value={value.current[0]}
							onChange={(date) => {
								onChange(createCustomTimeMachine(date!, value.current[1]));
								setInterval('custom');
								setStartDate(date);
							}}
						/>
					</Box>
					<Box width={140}>
						<Input.DatePicker
							value={value.current[1]}
							onChange={(date) => {
								onChange(createCustomTimeMachine(value.current[0], date!));
								setInterval('custom');
								setEndDate(date);
							}}
						/>
					</Box>
				</>
			) : (
				<Navigator>
					<IconButton
						type="secondary"
						icon={<ChevronLeftIcon />}
						disabled={value.previous === undefined}
						onClick={() => goTo(value.previous!)}
					/>
					<CompactSelect>
						<Select
							fit
							options={periodOptions}
							selectedValue={value.urlSafeCurrent[0]}
							selectedOption={{
								title: formatPeriodLabel(
									value.interval,
									moment(value.current[0]),
									moment(value.current[1]),
								),
								value: value.urlSafeCurrent[0],
							}}
							search={false}
							onChange={(cursor) => {
								if (cursor) {
									goTo(moment(cursor).toDate());
								}
							}}
						/>
					</CompactSelect>
					<IconButton
						type="secondary"
						icon={<ChevronRightIcon />}
						disabled={value.next === undefined}
						onClick={() => goTo(value.next!)}
					/>
				</Navigator>
			)}
		</FlexGap>
	);
}

DateIntervalNavigation.Skeleton = styled(Skeleton.Box)`
	height: 33px;
	width: 280px;
`;
